import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { push, replace } from 'connected-react-router';

import { openModalAction } from '../../../actions/modal';
import { RPM_PATIENT_STATUS, RPM_PORTAL_ACCESS_STATUS } from '../../../constants';
import Table3 from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import './patient.scss';
import { actions } from './redux/actions';
import { actions as cohortActions } from './Cohorts/redux/actions';
import { PATIENT_DETAILS_RESULT, PATIENT_LIST_FOR_SUGGESTION } from './redux/constants';
import { actions as scheduleActions } from '../Schedules/redux/actions';
import { ADD_SCHEDULE } from '../Schedules/redux/constants';
import { sendRestPasswordMessage } from '../../../actions/auth';
import { ORG_FEATURES, orgFeatureEnabled } from '../../../utils/userPermissions';
import { OnboardStages } from '../../../modals/OnboardPatientWizard/helpers';
import { getPatientColumns, getPatientOverviewColumns } from './patientColumnsDefinitions';
import { notificationActions } from '../../../components/Notification/redux/actions';
import PatientEnrollmentsDetails from './PatientEnrollmentsDetails';
import { createEnrollmentButtons, createPatientButtons, getPatientEnrollmentEntries } from './patientActionsDefinitions';

function PatientList(props) {
  const { 
    isLoading, 
    patients, 
    pagination, 
    cohortId, 
    status,
    onNavigate,
    onEditPatient,
    onSendMessage,
    getPatientsForSuggestion,
    forceRefresh,
    openConfirmModal,
    onEnrollPatient,
    startEnrollment,
    openResetPatientStatusModal,
    onAddMedicine,
    onOffsetChange,
    onPageSizeChange,
    onSortingChange,
    orgs,
    checkPatientEligibility,
    patientView,
    openCancelEnrollmentModal,
    openMoveToNotEnrolledModal,
  } = props;

  const showTransplantDetails = orgFeatureEnabled(ORG_FEATURES.transplant_details_enabled);
  const showShippingDetails = orgFeatureEnabled(ORG_FEATURES.shipper_enabled);
  const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);

  // Get the appropriate columns based on status and permissions
  const columns = React.useMemo(() => {
    if (patientView) {
      return getPatientOverviewColumns(showTransplantDetails, orgs?.length > 1);
    }
    return getPatientColumns(status, showTransplantDetails, showShippingDetails, orgs?.length > 1);
  }, [status, showTransplantDetails, showShippingDetails, orgs?.length, patientView]);

  const onPatientSelected = patient => {
    if (patient?.id) {
      const maskedId = encodeURIComponent(patient.id);
      onNavigate(`/cap-patients/${maskedId}`);
    }
  };

  const editPatient = patient => {
    onEditPatient(patient, forceRefresh, OnboardStages.editPatient);
  };

  const sendSMS = patient => {
    onSendMessage(patient, getPatientsForSuggestion, PATIENT_LIST_FOR_SUGGESTION);
  };

  const deletePatient = patient => {
    const deleteData = { maskedId: patient.masked_id, allowBilled: true };
    const data = {
      title: (
        <span>
          {Strings.deletePatientWarning}
          : 
          <b>{patient.patientName}</b>
          ? 
          <br />
          {' '}
          {Strings.warnigs.deletePatientWarning}
        </span>
      ),
      onConfirmAction: actions.deletePatient(deleteData, patient.id),
      onCancelAction: null,
      confirmPostAction: () => {
        forceRefresh();
      },
    };
    openConfirmModal(data);
  };

  const editEnrollment = context => {
    onEditPatient(
      context.patient,
      forceRefresh,
      OnboardStages.editEnrollment,
      context.enrollmentId,
    );
  };

  const enrollPatient = context => {
    const patientStatus = context.enrollment?.status;
    onEnrollPatient(
      context.patient,
      forceRefresh,
      patientStatus === RPM_PATIENT_STATUS.enrolled ? OnboardStages.activating : OnboardStages.enrolling,
      context.enrollmentId,
    );
  };

  const registerPatient = context => {
    onEnrollPatient(context.patient, forceRefresh, OnboardStages.registration, context.enrollmentId);
  };

  const endEnrollment = context => {
    onEnrollPatient(context.patient, forceRefresh, OnboardStages.settingEndDate, context.enrollmentId);
  };

  const handleStartEnrollment = context => {
    startEnrollment(context.patient, forceRefresh, context.enrollmentId);
  };

  const resetPatientStatus = patient => {
    openResetPatientStatusModal(patient, forceRefresh);
  };

  const reRegisterPatient = context => {
    const data = {
      title: (
        <span>
          {Strings.formatString(Strings.moveToOnboardedRegisteredWarning, context.patient.patientName)}
        </span>
      ),
      onConfirmAction: actions.clearNotEnrolled(context.patient.id, context.enrollmentId),
      confirmPostAction: forceRefresh,
      caption: Strings.register,
      hideCaution: true,
    };
    openConfirmModal(data);
  };

  const unregisterEnrollment = context => {
    if (!context.enrollmentId) {
      return;
    }
    openConfirmModal({
      title: <span>{Strings.formatString(Strings.unregisterEnrollmentWarning, context.patient.patientName)}</span>,
      onConfirmAction: actions.unregisterEnrollment(context.enrollmentId),
      confirmPostAction: forceRefresh,
      onSuccessNotification: Strings.formatString(Strings.enrollmentUnregistered, context.patient.patientName),
      caption: Strings.unregister,
      hideCaution: true,
    });
  };

  const cancelEnrollment = context => {
    if (!context.enrollmentId) {
      return;
    }
    openCancelEnrollmentModal(context.patient, forceRefresh, context.enrollmentId);
  };

  const rescheduleTelevisit = context => {
    onEditPatient(context.patient, forceRefresh, OnboardStages.rescheduleTelevisit, context.enrollmentId);
  };

  const changeOutreachStatus = context => {
    onEditPatient(context.patient, forceRefresh, OnboardStages.changeOutreachStatus, context.enrollmentId);
  };

  const addMedicine = context => {
    onAddMedicine(
      context.patient,
      context.enrollmentId,
      context.enrollment?.status === RPM_PATIENT_STATUS.activated,
    );
  };

  const unassignPatient = patient => {
    const data = {
      title: (
        <span>
          {Strings.unassignPatientWarning} 
          <b>{patient.patientName}</b>
          ?
        </span>
      ),
      onConfirmAction: cohortActions.unassignPatientFromCohort(patient.id, cohortId),
      confirmPostAction: forceRefresh,
      caption: Strings.removePatient,
      hideCaution: true,
    };
    openConfirmModal(data);
  };

  const inviteToPortal = patient => {
    const data = {
      title: <span>{Strings.formatString(Strings.invitePatientWarning, patient?.patientName)}</span>,
      caption: Strings.invitePatient,
      onConfirmAction: actions.invitePatientToPortal(patient.id, 'email'),
      onCancelAction: null,
      confirmPostAction: forceRefresh,
      onSuccessNotification: Strings.invitationSuccess,
      hideCaution: true,
    };
    openConfirmModal(data);
  };

  const resetPassword = patient => {
    const data = {
      title: (
        <span>
          {Strings.warnigs.adminResetPassword}
          <b>{patient.patientName}</b>
          ?
        </span>
      ),
      hideCaution: true,
      onConfirmAction: sendRestPasswordMessage({
        method: 'email',
        email: patient.email,
      }),
      onCancelAction: null,
      caption: 'Confirm password reset',
    };
    openConfirmModal(data);
  };

  const checkEligibility = patient => {
    checkPatientEligibility(patient, forceRefresh);
  };

  const moveToNotEnrolled = context => {
    openMoveToNotEnrolledModal(context.patient, forceRefresh, context.enrollmentId);
  };

  const enrollmentButtons = React.useMemo(() => createEnrollmentButtons({
    extendedOutreachStatusEnabled,
    endEnrollment,
    enrollPatient,
    handleStartEnrollment,
    registerPatient,
    resetPatientStatus,
    reRegisterPatient,
    rescheduleTelevisit,
    unregisterEnrollment,
    changeOutreachStatus,
    cancelEnrollment,
    addMedicine,
    checkEligibility,
    moveToNotEnrolled,
    editEnrollment,
  }), [cohortId, extendedOutreachStatusEnabled, showShippingDetails]);

  const patientButtons = React.useMemo(() => createPatientButtons({
    cohortId,
    sendSMS,
    checkEligibility,
    editPatient,
    unassignPatient,
    deletePatient,
  }), [cohortId]);

  return (
    <Table3
      isLoading={isLoading}
      name={`patient-list-${patientView ? 'patients' : status}-${orgs?.length > 1 ? 'with-orgs' : 'no-orgs'}`}
      data={patients || []}
      columns={columns}
      onRowClick={onPatientSelected}
      onOffsetChange={onOffsetChange}
      onPageSizeChange={onPageSizeChange}
      onSortingChange={onSortingChange}
      rowHeight={87.4219}
      pagination={pagination}
      buttons={patientView ? patientButtons : enrollmentButtons.concat(patientButtons)}
      enableColumnFiltering
      noScroll
      pinFirstColumn
      enableRowExpansion={patientView}
      renderExpandedRow={patientView
        ? patient => <PatientEnrollmentsDetails patient={patient} enrollmentButtons={enrollmentButtons} />
        : undefined}
    />
  );
}

PatientList.propTypes = {
  getPatients: PropTypes.func,
  isLoading: PropTypes.any,
  onEditPatient: PropTypes.func,
  onNavigate: PropTypes.func,
  openConfirmModal: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
    totalRecords: PropTypes.number,
  }),
  patients: PropTypes.array,
  cohortId: PropTypes.string,
  onEnrollPatient: PropTypes.func,
  onSendMessage: PropTypes.func,
  getPatientsForSuggestion: PropTypes.func,
  onAddMedicine: PropTypes.func,
  replacePath: PropTypes.func,
  startEnrollment: PropTypes.func,
  forceRefresh: PropTypes.func,
  status: PropTypes.string,
  patientView: PropTypes.bool,
  openResetPatientStatusModal: PropTypes.func,
  onSortingChange: PropTypes.func,
  onOffsetChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  orgs: PropTypes.array,
  checkPatientEligibility: PropTypes.func,
  getPatientEligibilityInfo: PropTypes.func,
  triggerEligibilityCheck: PropTypes.func,
  refreshEligibilityResults: PropTypes.func,
  openMoveToNotEnrolledModal: PropTypes.func,
  clearNotEnrolled: PropTypes.func,
  openCancelEnrollmentModal: PropTypes.func,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  return {
    patients: patients && patients?.data?.data,
    isLoading: patients && patients?.isLoading,
    pagination: patients && patients?.data?.pagination,
    orgs: state.entities?.orgFilter?.orgs,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: (path, state) => dispatch(push(path, state)),
  getPatientsForSuggestion: pageRequest => dispatch(actions.getPatientsForSuggestion(pageRequest)),
  // A list row lacks some patient fields (e.g. the Athena link), so the wizard gets the details.
  onEditPatient: (patient, nextAction, stage, selectedEnrollmentId) =>
    dispatch(actions.getPatientInfo(patient.id)).then(resp =>
      dispatch(
        openModalAction('onboard-patient', {
          onSuccess: nextAction,
          patient: resp?.type === PATIENT_DETAILS_RESULT ? resp.response : patient,
          stage,
          selectedEnrollmentId,
        }),
      ),
    ),
  onEnrollPatient: (patient, nextAction, stage, requestedEnrollmentId) =>
    dispatch(actions.getPatientInfo(patient.id)).then(resp => {
      const selectedEnrollmentId = requestedEnrollmentId || (patient?.patientEnrollment?.enrollments
        ? Object.keys(patient.patientEnrollment.enrollments)[0]
        : undefined);
      const patientData = resp?.type === PATIENT_DETAILS_RESULT ? resp.response : patient;
      return dispatch(
        openModalAction('onboard-patient', {
          patient: patientData,
          selectedEnrollmentId,
          onSuccess: nextAction,
          stage,
        }),
      );
    }),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  onSendMessage: (patient, loadOptionsAction, loadOptionsActionType) =>
    dispatch(
      openModalAction(
        'send-message',
        {
          patient,
          loadRecipients: loadOptionsAction,
          loadRecipientsActionType: loadOptionsActionType,
        },
        patient.id,
      ),
    ),
  onAddMedicine: (patient, enrollmentId, logTask) =>
    dispatch(
      openModalAction('edit-rpm-schedule', {
        patient,
        action: scheduleActions.addSchedule,
        actionType: ADD_SCHEDULE,
        logTask,
        enrollmentId,
      }),
    ),
  startEnrollment: (patient, onSuccess, selectedEnrollmentId) =>
    dispatch(
      openModalAction('start-enrollmend-and-schedules', {
        patient,
        onSuccess,
        selectedEnrollmentId,
      }),
    ),
  editPatient: (data, id) => dispatch(actions.editPatient(data, id)),
  endEnrollment: requestBody => dispatch(actions.endEnrollment(requestBody)),
  replacePath: path => dispatch(replace(path)),
  openResetPatientStatusModal: (patient, onSuccess) =>
    dispatch(actions.getPatientInfo(patient.id)).then(resp => {
      const patientData = resp?.type === PATIENT_DETAILS_RESULT ? resp.response : patient;
      return dispatch(openModalAction('reset-enrollment-and-unattach-devices', { patient: patientData, onSuccess }));
    }),
  unregisterPatient: (patient, onSuccess, changeState) => dispatch(openModalAction('unregister-patient', { patient, onSuccess, changeState })),
  checkPatientEligibility: (patient, onSuccess) => dispatch(openModalAction('check-eligibility', { patient, onSuccess })),
  openMoveToNotEnrolledModal: (patient, onSuccess, selectedEnrollmentId) =>
    dispatch(openModalAction('move-to-not-enrolled', { patient, onSuccess, selectedEnrollmentId })),
  openCancelEnrollmentModal: (patient, onSuccess, selectedEnrollmentId) =>
    dispatch(actions.getPatientInfo(patient.id)).then(resp => {
      if (resp?.type !== PATIENT_DETAILS_RESULT) {
        return dispatch(notificationActions.show(resp?.response?.data?.error?.message || Strings.errors.unknownError, 5000, true));
      }
      return dispatch(openModalAction('cancel-enrollment', { patient: resp.response, onSuccess, selectedEnrollmentId }));
    }),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientList);
